import type { BoxProps, BoxSx } from "@modernatx-internal/component-library";
import {
  Accordion,
  AccordionItem,
  Banner,
  Box,
  Button,
  Divider,
  FlagIcon,
  Grid,
  GridColumn,
  Icon,
  SidePanel,
  Table,
  TableBody,
  TableCell,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  Target,
  TargetProvider,
  Text,
  ThemeOverride,
  ThemeProvider,
  UniformGridProvider,
  Video as UIKitVideo,
  VideoChapters,
  VideoInformation,
  VideoPlayer
} from "@modernatx-internal/component-library";
import { CardContent as PatternCardContent } from "@modernatx-internal/website-pattern-library";
import dynamic from "next/dynamic";
import { useSearchParams } from "next/navigation";
import React from "react";
import { Descendant } from "slate";
import { RenderElementProps, RenderLeafProps } from "slate-react";
import { Global } from "theme-ui";

import { RenderMetadata } from "@/components/RenderMetadata";
import {
  DisplayTable,
  DisplayTableBody,
  DisplayTableCell,
  DisplayTableHeader,
  DisplayTableHeaderCell,
  DisplayTableRow,
  DisplayTableRowHeaderCell
} from "@/content-V2/components/DisplayTable";
import { PrescribingInfoModal } from "@/content-V2/components/PrescribingInfoModal";
import type { VideoData } from "@/content-V2/components/StreamingVideo";
import { VideoModal } from "@/content-V2/components/VideoModal";
import { ContentPage as ContentPageProps } from "@/content-V2/types";
import { replaceContentComponents } from "@/content-V2/utils/replaceContentComponents";
import { useExperience } from "@/context/ExperienceContext";
import { ExitModalProvider } from "@/hooks/useExitModal";
import { useInitializeRouter } from "@/hooks/useInitializeRouter";
import { useTargetProvider } from "@/hooks/useTargetProvider";

import { ContentPage } from "../../components/ContentPage";
import { TextBlock } from "../../components/TextBlock/TextBlock";
import { ActionProvider } from "../hooks/useAction";
import { BarChart } from "./BarChart";
import { CardGroup, CardGroupItem } from "./CardGroup";
import { ContentStrip } from "./ContentStrip";
import { ExitModal } from "./ExitModal";
import { FeatureCard } from "./FeatureCard";
import { FinderSearchBar } from "./FinderSearchBar";
import { Footer } from "./Footer";
import { Header } from "./Header";
import { HeroBanner } from "./HeroBanner";
import { Hexagon } from "./Hexagon";
import { IconCard, IconCardGroup } from "./IconCard";
import { InquiryForm } from "./InquiryForm";
import { ISIPanel } from "./ISIPanel";
import { Link } from "./Link";
import { LocaleSelector } from "./LocaleSelector";
import { LocationFinder } from "./LocationFinder";
import { NavBar } from "./NavBar";
import { NavBarAnchor } from "./NavBarAnchor";
import { NavBarProvider } from "./NavBarContext";
import { NavigationBanner } from "./NavigationBanner";
import { NewsletterForm } from "./NewsletterForm";
import { PageFooter } from "./PageFooter";
import { PageHeader } from "./PageHeader";
import { PageHeaderLink } from "./PageHeaderLink";
import { PageHeaderSubnavLink } from "./PageHeaderSubnavLink";
import { PageImage } from "./PageImage";
import { PDFViewer } from "./PDFViewer";
import { PrefectureFinderForm } from "./PrefectureFinderForm";
import { ProvinceEligibilitySelector } from "./ProvinceEligibilitySelector";
import { ResourceList } from "./ResourceList";
import { ResultCard } from "./ResultCard";
import { SimpleNewsletterForm } from "./SimpleNewsletterForm";
import { SlideOutPanel, SlideOutPanelButton, SlideOutPanelProvider } from "./SlideOutPanel";
import { StickyProvider } from "./Sticky";
import { Tab, Tabs } from "./Tabs";

const PageControls = dynamic(
  () => import("../../components/PageControls").then((res) => res.PageControls),
  {
    ssr: false
  }
);

const TextBlockPopoverV2 = dynamic(
  () =>
    import("@/components/TextBlockPopover-V2/TextBlockPopover").then((res) => res.TextBlockPopover),
  {
    ssr: false
  }
);

const StreamingVideo = dynamic(
  () => import("@/content-V2/components/StreamingVideo").then((res) => res.StreamingVideo),
  {
    ssr: true
  }
);

const FinderProduct = dynamic(
  () => import("@/content-V2/components/Finder/Finder").then((res) => res.Finder),
  {
    ssr: true
  }
);

const FinderPrefectureFinder = dynamic(
  () =>
    import("@/content-V2/components/Finder/PrefectureFinder").then((res) => res.PrefectureFinder),
  {
    ssr: true
  }
);

const FinderFooter = dynamic(
  () => import("@/content-V2/components/Finder/FinderFooter").then((res) => res.FinderFooter),
  {
    ssr: true
  }
);

const AtlasNavBar = dynamic(
  () => import("@/content-V2/components/Atlas/AtlasNavBar").then((res) => res.AtlasNavBar),
  {
    ssr: true
  }
);

const AtlasFooter = dynamic(
  () => import("@/content-V2/components/Atlas/AtlasFooter").then((res) => res.AtlasFooter),
  {
    ssr: true
  }
);

const AtlasSoftGateModal = dynamic(
  () =>
    import("@/content-V2/components/Atlas/AtlasSoftGateModal").then(
      (res) => res.AtlasSoftGateModal
    ),
  {
    ssr: false
  }
);

const Bench2PracticeLogo = dynamic<BoxProps>(
  () =>
    import("@modernatx-internal/component-library/logo/Bench2PracticeLogo").then(
      (res) => res.Bench2PracticeLogo
    ),
  {
    ssr: true
  }
);

const MFlusivaLogo = dynamic<BoxProps>(
  () =>
    import("@modernatx-internal/component-library/logo/MFlusivaLogo").then(
      (res) => res.MFlusivaLogo
    ),
  {
    ssr: true
  }
);

const MNexspikeByModernaLogo = dynamic<BoxProps>(
  () =>
    import("@modernatx-internal/component-library/logo/MNexspikeByModernaLogo").then(
      (res) => res.MNexspikeByModernaLogo
    ),
  {
    ssr: true
  }
);

const MNexspikeByModernaVerticalLogo = dynamic<BoxProps>(
  () =>
    import("@modernatx-internal/component-library/logo/MNexspikeByModernaVerticalLogo").then(
      (res) => res.MNexspikeByModernaVerticalLogo
    ),
  {
    ssr: true
  }
);

const MNexspikeLogo = dynamic<BoxProps>(
  () =>
    import("@modernatx-internal/component-library/logo/MNexspikeLogo").then(
      (res) => res.MNexspikeLogo
    ),
  {
    ssr: true
  }
);

const ModernaAtlasLogo = dynamic<BoxProps>(
  () =>
    import("@modernatx-internal/component-library/logo/ModernaAtlasLogo").then(
      (res) => res.ModernaAtlasLogo
    ),
  {
    ssr: true
  }
);

const ModernaLogo = dynamic<BoxProps>(
  () =>
    import("@modernatx-internal/component-library/logo/ModernaLogo").then((res) => res.ModernaLogo),
  {
    ssr: true
  }
);

const ModernaLogoWhite = dynamic<BoxProps>(
  () =>
    import("@modernatx-internal/component-library/logo/ModernaLogoWhite").then(
      (res) => res.ModernaLogoWhite
    ),
  {
    ssr: true
  }
);

const MResviaLogo = dynamic<BoxProps>(
  () =>
    import("@modernatx-internal/component-library/logo/MResviaLogo").then((res) => res.MResviaLogo),
  {
    ssr: true
  }
);

const MRNAAccessLogo = dynamic<BoxProps>(
  () =>
    import("@modernatx-internal/component-library/logo/MRNAAccessLogo").then(
      (res) => res.MRNAAccessLogo
    ),
  {
    ssr: true
  }
);

const SpikevaxByModernaLogo = dynamic<BoxProps>(
  () =>
    import("@modernatx-internal/component-library/logo/SpikevaxByModernaLogo").then(
      (res) => res.SpikevaxByModernaLogo
    ),
  { ssr: true }
);

const SpikevaxElasomeranLogo = dynamic<BoxProps>(
  () =>
    import("@modernatx-internal/component-library/logo/SpikevaxElasomeranLogo").then(
      (res) => res.SpikevaxElasomeranLogo
    ),
  { ssr: true }
);

const SpikevaxFranceLogo = dynamic<BoxProps>(
  () =>
    import("@modernatx-internal/component-library/logo/SpikevaxFranceLogo").then(
      (res) => res.SpikevaxFranceLogo
    ),
  {
    ssr: true
  }
);

const SpikevaxKoreaLogo = dynamic<BoxProps>(
  () =>
    import("@modernatx-internal/component-library/logo/SpikevaxKoreaLogo").then(
      (res) => res.SpikevaxKoreaLogo
    ),
  {
    ssr: true
  }
);

const SpikevaxLogo = dynamic<BoxProps>(
  () =>
    import("@modernatx-internal/component-library/logo/SpikevaxLogo").then(
      (res) => res.SpikevaxLogo
    ),
  {
    ssr: true
  }
);

const SpikevaxRegisteredLogo = dynamic<BoxProps>(
  () =>
    import("@modernatx-internal/component-library/logo/SpikevaxRegisteredLogo").then(
      (res) => res.SpikevaxRegisteredLogo
    ),
  { ssr: true }
);

const SpikevaxSimpleLogo = dynamic<BoxProps>(
  () =>
    import("@modernatx-internal/component-library/logo/SpikevaxSimpleLogo").then(
      (res) => res.SpikevaxSimpleLogo
    ),
  {
    ssr: true
  }
);

interface PagePropsWithExperience {
  content: ContentPageProps | null;
  fixInitialRouteNotPushed?: boolean;
  textBlocks: Record<string, Descendant[]> | null;
  videoData?: Record<string, VideoData> | null;
}

export const PageContent: React.FC<PagePropsWithExperience> = ({
  content,
  fixInitialRouteNotPushed = true,
  textBlocks,
  videoData
}) => {
  const { environment, country, language } = useExperience();
  const { expandedMode } = useExperience();
  const { handleClick, resolveTarget } = useTargetProvider();
  const canRenderContent = !!country && !!content && typeof content.theme === "object";
  useInitializeRouter({ disabled: !fixInitialRouteNotPushed || !canRenderContent });

  const background = (
    <Global
      styles={{
        html: {
          backgroundColor: "white"
        }
      }}
    />
  );
  const searchParams = useSearchParams();
  const editing = !!searchParams.get("edit") && environment === "local";
  const shouldRenderPageControls = environment === "local" || environment === "preview";
  const getPropsWithoutChildren = React.useCallback(
    <T extends object>(props: T): Omit<T, "children"> => {
      const nextProps = { ...props };
      delete (nextProps as { children?: unknown }).children;

      return nextProps;
    },
    []
  );

  const renderElement = React.useCallback((props: RenderElementProps) => {
    const { attributes, children, element: elementInitial } = props;
    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      children: _childrenOmitted,
      hangingIndent,
      type,
      // @ts-ignore
      textVariant,
      ...element
    } = elementInitial;
    // @ts-ignore
    const styles: BoxSx = {
      display: type === "span" ? "block" : null,
      ...(hangingIndent
        ? {
            paddingInlineStart: () => "0.7em",
            textIndent: "-0.7em"
          }
        : {}),
      ...element
    };

    switch (type) {
      case "li-text":
        return (
          <Box as="div" {...attributes} sx={styles}>
            {children}
          </Box>
        );
      case "li":
        return (
          <Box as="li" {...attributes} sx={styles}>
            {children}
          </Box>
        );
      case "ol":
        const listItemsCount = React.Children.count(children);
        const listItemDigits = listItemsCount.toString().length;

        return (
          <Box
            as="ol"
            {...attributes}
            sx={{
              ...styles,
              listStyle: "revert",
              paddingInlineStart: () => `${listItemDigits * 0.5 + 0.8}em`
            }}
          >
            {children}
          </Box>
        );
      case "ul":
        return (
          <Box
            as="ul"
            {...attributes}
            sx={{
              ...styles,
              paddingInlineStart: () => "1.2em"
            }}
          >
            {children}
          </Box>
        );
      default:
        return (
          <Text
            as={type as React.ComponentProps<typeof Text>["as"]}
            {...attributes}
            sx={styles}
            variant={textVariant}
          >
            {children}
          </Text>
        );
    }
  }, []);

  const renderLeaf = React.useCallback(
    (props: RenderLeafProps) => {
      const { leaf: leafInitial } = props;
      const {
        fontSize,
        lineHeight,
        skipExitModal,
        type,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        text: _omittedText,
        verticalAlign,
        ...leaf
      } = leafInitial;
      const styles: BoxSx = {
        ...leaf,
        // @ts-ignore
        lineHeight,
        // @ts-ignore
        fontSize,
        verticalAlign
      };

      let text = props.children;

      if (typeof props.children === "string") {
        text = props.children?.split("\n").reduce((acc: React.ReactNode[], segment, i) => {
          if (i === 0) {
            return [segment];
          }
          return [...acc, <br key={i} />, segment];
        }, []);
      }

      if (type === "sub") {
        return (
          <Box as="span" sx={styles} {...props.attributes}>
            <Box as="sub" sx={{ fontSize: () => "55%", verticalAlign: "revert" }}>
              {text}
            </Box>
          </Box>
        );
      }

      if (type === "sup") {
        return (
          <Box as="span" sx={styles} {...props.attributes}>
            <Box as="sup" sx={{ fontSize: () => "55%", verticalAlign: "revert" }}>
              {text}
            </Box>
          </Box>
        );
      }

      if (leaf.href) {
        return (
          <Link
            href={editing ? undefined : leaf.href}
            sx={{ display: "inline", ...styles }}
            data-skip-exit-modal={skipExitModal ? "true" : undefined}
            {...props.attributes}
          >
            {text}
          </Link>
        );
      }

      return (
        <Box as={type || "span"} sx={styles} {...props.attributes}>
          {text}
        </Box>
      );
    },
    [editing]
  );

  if (!canRenderContent) {
    return (
      <ThemeProvider>
        {background}
        <Grid>
          <GridColumn
            sx={{
              alignItems: "center",
              display: "flex",
              height: "100vh",
              justifyContent: "center"
            }}
          >
            <Text as="h1" variant="title-1-medium">
              {!country || !content
                ? "The site is not available in your country."
                : "A theme object is required"}
            </Text>
          </GridColumn>
        </Grid>
        {shouldRenderPageControls && <PageControls />}
      </ThemeProvider>
    );
  }

  return (
    <ContentPage
      renderElement={renderElement}
      renderLeaf={renderLeaf}
      renderTextBlockPopover={() => (
        <ThemeOverride themeBrand={content.theme}>
          <TextBlockPopoverV2 />
        </ThemeOverride>
      )}
      textBlocks={textBlocks}
    >
      <TargetProvider clickHandler={handleClick} targetResolver={resolveTarget}>
        <ThemeProvider language={language} themeBranding={content.theme}>
          <ActionProvider>
            <SlideOutPanelProvider>
              <Global
                styles={{
                  html: {
                    // @ts-ignore
                    backgroundColor: ({ colors }) => `${colors[content.backgroundColor]} !important`
                  },
                  body: {
                    overflowX: "hidden"
                  }
                }}
              />
              <RenderMetadata
                {...content.metadata}
                renderGoogleRecaptcha={!!content.metadata.googleRecaptcha}
              />
              {replaceContentComponents<React.ReactNode>(content.children, (component, index) => {
                const key = component.props.key || index;

                delete component.props.key;

                // Disable links when in editing mode
                if (editing) {
                  if ("href" in component.props) {
                    delete component.props.href;
                  }
                  if ("onClick" in component.props) {
                    delete component.props.onClick;
                  }
                }

                switch (component.type) {
                  case "Accordion":
                    return (
                      <Accordion
                        key={key}
                        {...(component.props as React.ComponentProps<typeof Accordion>)}
                        expandAll={expandedMode}
                      />
                    );
                  case "AccordionItem":
                    return (
                      <AccordionItem
                        key={key}
                        {...(component.props as React.ComponentProps<typeof AccordionItem>)}
                      />
                    );
                  case "AtlasFooter":
                    return <AtlasFooter key={key} {...component.props} />;
                  case "AtlasNavBar":
                    return <AtlasNavBar key={key} {...component.props} />;
                  case "AtlasSoftGateModal":
                    return <AtlasSoftGateModal key={key} {...component.props} />;
                  case "Banner":
                    return <Banner key={key} {...component.props} />;
                  case "BarChart":
                    return <BarChart key={key} {...component.props} />;
                  case "Box":
                    return (
                      <Box key={key} {...(component.props as React.ComponentProps<typeof Box>)} />
                    );
                  case "Button":
                    return (
                      <Button
                        key={key}
                        {...(component.props as React.ComponentProps<typeof Button>)}
                      />
                    );
                  case "CardContent":
                    return (
                      <PatternCardContent
                        key={key}
                        {...(component.props as React.ComponentProps<typeof PatternCardContent>)}
                      />
                    );
                  case "CardGroup":
                    return <CardGroup key={key} {...component.props} />;
                  case "CardGroupItem":
                    return <CardGroupItem key={key} {...component.props} />;
                  case "ContentStrip":
                    return <ContentStrip key={key} {...component.props} />;
                  case "DisplayTable":
                    return (
                      <DisplayTable
                        key={key}
                        {...(component.props as React.ComponentProps<typeof DisplayTable>)}
                      />
                    );
                  case "DisplayTableHeader":
                    return (
                      <DisplayTableHeader
                        key={key}
                        {...(component.props as React.ComponentProps<typeof DisplayTableHeader>)}
                      />
                    );
                  case "DisplayTableHeaderCell":
                    return <DisplayTableHeaderCell key={key} {...component.props} />;
                  case "DisplayTableBody":
                    return (
                      <DisplayTableBody
                        key={key}
                        {...(component.props as React.ComponentProps<typeof DisplayTableBody>)}
                      />
                    );
                  case "DisplayTableRow":
                    return (
                      <DisplayTableRow
                        key={key}
                        {...(component.props as React.ComponentProps<typeof DisplayTableRow>)}
                      />
                    );
                  case "DisplayTableCell":
                    return <DisplayTableCell key={key} {...component.props} />;
                  case "DisplayTableRowHeaderCell":
                    return <DisplayTableRowHeaderCell key={key} {...component.props} />;
                  case "Divider":
                    return <Divider key={key} {...component.props} />;
                  case "ExitModal":
                    return <ExitModal key={key} {...component.props} />;
                  case "FeatureCard":
                    return <FeatureCard key={key} {...component.props} />;
                  case "PrefectureFinderForm":
                    return <PrefectureFinderForm key={key} {...component.props} />;
                  case "PrefectureFinder":
                    return <FinderPrefectureFinder key={key} {...component.props} />;
                  case "FinderFooter":
                    return <FinderFooter key={key} {...component.props} />;
                  case "FinderSearchBar":
                    return <FinderSearchBar key={key} {...component.props} />;
                  case "LocationFinder":
                    return <LocationFinder key={key} {...component.props} />;
                  case "Footer":
                    return <Footer key={key} {...component.props} />;
                  case "FlagIcon": {
                    const flagIconProps = getPropsWithoutChildren(component.props);
                    return (
                      <FlagIcon
                        key={key}
                        {...(flagIconProps as React.ComponentProps<typeof FlagIcon>)}
                      />
                    );
                  }
                  case "Grid":
                    return (
                      <Grid key={key} {...(component.props as React.ComponentProps<typeof Grid>)} />
                    );
                  case "GridColumn":
                    return (
                      <GridColumn
                        key={key}
                        {...(component.props as React.ComponentProps<typeof GridColumn>)}
                      />
                    );
                  case "Header":
                    return <Header key={key} {...component.props} />;
                  case "HeroBanner":
                    return (
                      <HeroBanner
                        key={key}
                        {...(component.props as React.ComponentProps<typeof HeroBanner>)}
                      />
                    );
                  case "Hexagon":
                    return <Hexagon key={key} {...component.props} />;
                  case "Icon":
                    return <Icon key={key} {...component.props} />;
                  case "IconCard":
                    return <IconCard key={key} {...component.props} />;
                  case "IconCardGroup":
                    return <IconCardGroup key={key} {...component.props} />;
                  case "Image":
                    return <PageImage key={key} {...component.props} />;
                  case "ISIPanel":
                    return <ISIPanel key={key} {...component.props} />;
                  case "Link":
                    return (
                      <Link key={key} {...(component.props as React.ComponentProps<typeof Link>)} />
                    );
                  case "LocaleSelector":
                    return <LocaleSelector key={key} {...component.props} />;
                  case "Bench2PracticeLogo": {
                    const logoProps = getPropsWithoutChildren(component.props);
                    return (
                      <Bench2PracticeLogo
                        key={key}
                        {...(logoProps as React.ComponentProps<typeof Bench2PracticeLogo>)}
                      />
                    );
                  }
                  case "MFlusivaLogo": {
                    const logoProps = getPropsWithoutChildren(component.props);
                    return (
                      <MFlusivaLogo
                        key={key}
                        {...(logoProps as React.ComponentProps<typeof MFlusivaLogo>)}
                      />
                    );
                  }
                  case "MNexspikeByModernaLogo": {
                    const logoProps = getPropsWithoutChildren(component.props);
                    return (
                      <MNexspikeByModernaLogo
                        key={key}
                        {...(logoProps as React.ComponentProps<typeof MNexspikeByModernaLogo>)}
                      />
                    );
                  }
                  case "MNexspikeByModernaVerticalLogo": {
                    const logoProps = getPropsWithoutChildren(component.props);
                    return (
                      <MNexspikeByModernaVerticalLogo
                        key={key}
                        {...(logoProps as React.ComponentProps<
                          typeof MNexspikeByModernaVerticalLogo
                        >)}
                      />
                    );
                  }
                  case "MNexspikeLogo": {
                    const logoProps = getPropsWithoutChildren(component.props);
                    return (
                      <MNexspikeLogo
                        key={key}
                        {...(logoProps as React.ComponentProps<typeof MNexspikeLogo>)}
                      />
                    );
                  }
                  case "ModernaAtlasLogo": {
                    const logoProps = getPropsWithoutChildren(component.props);
                    return (
                      <ModernaAtlasLogo
                        key={key}
                        {...(logoProps as React.ComponentProps<typeof ModernaAtlasLogo>)}
                      />
                    );
                  }
                  case "ModernaLogo": {
                    const logoProps = getPropsWithoutChildren(component.props);
                    return (
                      <ModernaLogo
                        key={key}
                        {...(logoProps as React.ComponentProps<typeof ModernaLogo>)}
                      />
                    );
                  }
                  case "ModernaLogoWhite": {
                    const logoProps = getPropsWithoutChildren(component.props);
                    return (
                      <ModernaLogoWhite
                        key={key}
                        {...(logoProps as React.ComponentProps<typeof ModernaLogoWhite>)}
                      />
                    );
                  }
                  case "MResviaLogo": {
                    const logoProps = getPropsWithoutChildren(component.props);
                    return (
                      <MResviaLogo
                        key={key}
                        {...(logoProps as React.ComponentProps<typeof MResviaLogo>)}
                      />
                    );
                  }
                  case "MRNAAccessLogo": {
                    const logoProps = getPropsWithoutChildren(component.props);
                    return (
                      <MRNAAccessLogo
                        key={key}
                        {...(logoProps as React.ComponentProps<typeof MRNAAccessLogo>)}
                      />
                    );
                  }
                  case "SpikevaxByModernaLogo": {
                    const logoProps = getPropsWithoutChildren(component.props);
                    return (
                      <SpikevaxByModernaLogo
                        key={key}
                        {...(logoProps as React.ComponentProps<typeof SpikevaxByModernaLogo>)}
                      />
                    );
                  }
                  case "SpikevaxElasomeranLogo": {
                    const logoProps = getPropsWithoutChildren(component.props);
                    return (
                      <SpikevaxElasomeranLogo
                        key={key}
                        {...(logoProps as React.ComponentProps<typeof SpikevaxElasomeranLogo>)}
                      />
                    );
                  }
                  case "SpikevaxFranceLogo": {
                    const logoProps = getPropsWithoutChildren(component.props);
                    return (
                      <SpikevaxFranceLogo
                        key={key}
                        {...(logoProps as React.ComponentProps<typeof SpikevaxFranceLogo>)}
                      />
                    );
                  }
                  case "SpikevaxKoreaLogo": {
                    const logoProps = getPropsWithoutChildren(component.props);
                    return (
                      <SpikevaxKoreaLogo
                        key={key}
                        {...(logoProps as React.ComponentProps<typeof SpikevaxKoreaLogo>)}
                      />
                    );
                  }
                  case "SpikevaxLogo": {
                    const logoProps = getPropsWithoutChildren(component.props);
                    return (
                      <SpikevaxLogo
                        key={key}
                        {...(logoProps as React.ComponentProps<typeof SpikevaxLogo>)}
                      />
                    );
                  }
                  case "SpikevaxRegisteredLogo": {
                    const logoProps = getPropsWithoutChildren(component.props);
                    return (
                      <SpikevaxRegisteredLogo
                        key={key}
                        {...(logoProps as React.ComponentProps<typeof SpikevaxRegisteredLogo>)}
                      />
                    );
                  }
                  case "SpikevaxSimpleLogo": {
                    const logoProps = getPropsWithoutChildren(component.props);
                    return (
                      <SpikevaxSimpleLogo
                        key={key}
                        {...(logoProps as React.ComponentProps<typeof SpikevaxSimpleLogo>)}
                      />
                    );
                  }
                  case "NavBar":
                    return (
                      <NavBar
                        key={key}
                        {...(component.props as React.ComponentProps<typeof NavBar>)}
                      />
                    );
                  case "NavigationBanner":
                    return <NavigationBanner key={key} {...component.props} />;
                  case "NavBarAnchor":
                    return (
                      <NavBarAnchor
                        key={key}
                        {...(component.props as React.ComponentProps<typeof NavBarAnchor>)}
                      />
                    );
                  case "NewsletterForm":
                    return <NewsletterForm key={key} {...component.props} />;
                  case "SimpleNewsletterForm":
                    return <SimpleNewsletterForm key={key} {...component.props} />;
                  case "InquiryForm":
                    return <InquiryForm key={key} {...component.props} />;
                  case "PDFViewer":
                    return <PDFViewer key={key} {...component.props} />;
                  case "ProductFinder":
                    return <FinderProduct key={key} {...component.props} />;
                  case "ProvinceEligibilitySelector":
                    return <ProvinceEligibilitySelector key={key} {...component.props} />;
                  case "Null":
                    return null;
                  case "PageFooter":
                    return <PageFooter key={key} {...component.props} />;
                  case "PageHeader":
                    return <PageHeader key={key} {...component.props} />;
                  case "PageHeaderLink":
                    return <PageHeaderLink key={key} {...component.props} />;
                  case "PageHeaderSubnavLink":
                    return <PageHeaderSubnavLink key={key} {...component.props} />;
                  case "PrescribingInfoModal":
                    return <PrescribingInfoModal key={key} {...component.props} />;
                  case "ResourceList":
                    return <ResourceList key={key} {...component.props} />;
                  case "ResultCard":
                    return <ResultCard key={key} {...component.props} />;
                  case "SidePanel":
                    return (
                      <SidePanel
                        key={key}
                        {...(component.props as React.ComponentProps<typeof SidePanel>)}
                      />
                    );
                  case "SlideOutPanel":
                    return <SlideOutPanel key={key} {...component.props} />;
                  case "SlideOutPanelButton":
                    return <SlideOutPanelButton key={key} {...component.props} />;
                  case "Tab":
                    return <Tab key={key} {...component.props} />;
                  case "Tabs":
                    return <Tabs key={key} {...component.props} />;
                  case "Table":
                    return (
                      <Table
                        key={key}
                        {...(component.props as React.ComponentProps<typeof Table>)}
                      />
                    );
                  case "TableCell":
                    return <TableCell key={key} {...component.props} />;
                  case "TableRow":
                    return (
                      <TableRow
                        key={key}
                        {...(component.props as React.ComponentProps<typeof TableRow>)}
                      />
                    );
                  case "TableBody":
                    return <TableBody key={key} {...component.props} />;
                  case "TableHeaderRow":
                    return <TableHeaderRow key={key} {...component.props} />;
                  case "TableHeaderCell":
                    return <TableHeaderCell key={key} {...component.props} />;
                  case "Target":
                    return (
                      <Target
                        key={key}
                        {...(component.props as React.ComponentProps<typeof Target>)}
                      />
                    );
                  case "Text":
                    return <Text key={key} {...component.props} />;
                  case "TextBlock":
                    return (
                      <TextBlock
                        key={key}
                        {...(component.props as React.ComponentProps<typeof TextBlock>)}
                      />
                    );
                  case "ThemeOverride":
                    return <ThemeOverride key={key} {...component.props} />;
                  case "UniformGridProvider":
                    return <UniformGridProvider key={key} {...component.props} />;
                  case "LegacyVideo": {
                    const { children, source, ...legacyVideoProps } = component.props;

                    return (
                      <UIKitVideo
                        key={key}
                        sources={[{ src: source, type: "video/mp4" }]}
                        {...legacyVideoProps}
                      >
                        {Array.isArray(children) && children.length === 0 ? null : children}
                      </UIKitVideo>
                    );
                  }
                  case "Video": {
                    const { children, streamingPath, ...videoProps } = component.props;

                    if (streamingPath) {
                      return (
                        <StreamingVideo
                          key={key}
                          streamingPath={streamingPath}
                          videoData={videoData?.[streamingPath]}
                          {...videoProps}
                        >
                          {Array.isArray(children) && children.length === 0 ? null : children}
                        </StreamingVideo>
                      );
                    }

                    return null;
                  }
                  case "VideoModal": {
                    return <VideoModal key={key} {...component.props} />;
                  }
                  case "VideoChapters":
                    return <VideoChapters key={key} {...component.props} />;
                  case "VideoInformation":
                    return <VideoInformation key={key} {...component.props} />;
                  case "VideoPlayer":
                    return <VideoPlayer key={key} {...component.props} />;
                }
              })}
            </SlideOutPanelProvider>
          </ActionProvider>
        </ThemeProvider>
      </TargetProvider>
      {shouldRenderPageControls && <PageControls />}
    </ContentPage>
  );
};

export const Page: React.FC<PagePropsWithExperience> = (props) => (
  <ExitModalProvider>
    <StickyProvider>
      <NavBarProvider>
        <PageContent {...props} />
      </NavBarProvider>
    </StickyProvider>
  </ExitModalProvider>
);
